import { useEffect, useRef, useMemo, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'

import {
  getOrgEventsForReport,
  getPricingLevelPerformance,
} from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'
import { EventSelector } from '../EventSelector'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { ReportRefreshButton } from '../ReportRefreshButton'
import PricingLevelExecutiveSummary from './PricingLevelExecutiveSummary'
import PricingLevelForecastedRevenue from './PricingLevelForecastedRevenue'
import PricingLevelInsightsRow from './PricingLevelInsightsRow'
import PricingLevelPerformanceMatrix from './PricingLevelPerformanceMatrix'
import PricingLevelRevenueLeakage from './PricingLevelRevenueLeakage'

import './pricingLevelPerformanceWrapper.scss'

export default function PricingLevelPerformanceWrapper({ organization }) {
  const [events, setEvents] = useState([])
  const [eventUUID, setEventUUID] = useState('')
  const [reportData, setReportData] = useState(null)
  const [isLoadingEvents, setIsLoadingEvents] = useState(true)
  const [isLoadingReport, setIsLoadingReport] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingReport)
  const [loadError, setLoadError] = useState(null)
  const [showTutorial, setShowTutorial] = useState(false)
  const tutorialVideoRef = useRef(null)

  const organizationUUID = organization?.uuid

  const TUTORIAL_VIDEO_SOURCE =
    'https://blocktickets.nyc3.cdn.digitaloceanspaces.com/tutorials/Pricing%20Level%20Performance_1080p.mp4'

  const handleCloseTutorial = () => {
    if (tutorialVideoRef.current) {
      tutorialVideoRef.current.pause()
      tutorialVideoRef.current.currentTime = 0
    }
    setShowTutorial(false)
  }

  useEffect(() => {
    if (!organizationUUID) return

    let cancelled = false
    setIsLoadingEvents(true)

    getOrgEventsForReport(organizationUUID)
      .then((res) => {
        if (cancelled) return
        const list = Array.isArray(res?.data) ? res.data : []
        setEvents(list)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load events', err)
        setLoadError('Could not load events for this organization.')
      })
      .finally(() => {
        if (!cancelled) setIsLoadingEvents(false)
      })

    return () => {
      cancelled = true
    }
  }, [organizationUUID])

  useEffect(() => {
    if (!organizationUUID || !eventUUID) {
      setReportData(null)
      return
    }

    let cancelled = false
    setIsLoadingReport(true)
    setLoadError(null)

    getPricingLevelPerformance(organizationUUID, eventUUID)
      .then((res) => {
        if (cancelled) return
        setReportData(res?.data ?? null)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load pricing level performance', err)
        setLoadError('Could not load pricing level performance for this event.')
        setReportData(null)
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingReport(false)
          completeRefresh()
        }
      })

    return () => {
      cancelled = true
    }
  }, [organizationUUID, eventUUID, refreshToken])

  const selectedEvent = useMemo(
    () => events.find((event) => event.uuid === eventUUID) || null,
    [events, eventUUID]
  )

  const rows = reportData?.rows || []
  const isLoading = isLoadingEvents || isLoadingReport

  return (
    <section
      className="spacer-sm pricing-level-performance-wrapper"
      id="pricing-level-performance-wrapper"
    >
      <header className="section-header-sm report-page-header">
        <div className="section-heading">
          <h1>Pricing Level Performance</h1>
        </div>
        <p className="section-header-desc">
          Compare sell-through and pace by pricing level for{' '}
          {organization?.name || 'your organization'}. Status colors highlight
          where inventory is strong, needs attention, or is at risk.
        </p>
        <p className="section-header-desc mt-2">
          <strong>Note: </strong>
          <Button
            variant="link"
            className="p-0 align-baseline"
            onClick={() => setShowTutorial(true)}
          >
            View Tutorial
          </Button>
        </p>
      </header>

      <Modal
        show={showTutorial}
        onHide={handleCloseTutorial}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Pricing Level Performance Tutorial</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <video
            ref={tutorialVideoRef}
            controls
            autoPlay
            style={{ width: '100%' }}
          >
            <source src={TUTORIAL_VIDEO_SOURCE} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </Modal.Body>
      </Modal>

      <Card
        body
        className="card--xs card-with-border pricing-level-performance-controls"
      >
        <Stack
          direction="horizontal"
          gap={3}
          className="align-items-end flex-wrap"
        >
          <EventSelector
            events={events}
            selected={eventUUID}
            setSelected={setEventUUID}
            isLoading={isLoadingEvents}
            groupByYear
          />
          <ReportRefreshButton
            onRefresh={refresh}
            isRefreshing={isLoadingReport}
            disabled={!eventUUID || isLoadingReport}
          />
        </Stack>
        {selectedEvent && (
          <p className="text-muted small mb-0 mt-2">
            Showing latest snapshot for <strong>{selectedEvent.name}</strong>
            {reportData?.snapshotDate && (
              <>
                {' '}
                · updated{' '}
                {new Date(reportData.snapshotDate).toLocaleDateString(
                  undefined,
                  {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  }
                )}
              </>
            )}
          </p>
        )}
      </Card>

      {loadError && (
        <Card body className="card--xs card-with-border mt-3">
          <p className="text-danger mb-0">{loadError}</p>
        </Card>
      )}

      {isLoading && eventUUID && <PageLoadingContainer />}

      {!isLoading && eventUUID && reportData?.error === 'event_not_found' && (
        <Card body className="card--xs card-with-border mt-3">
          <p className="text-muted mb-0">
            Event not found for this organization.
          </p>
        </Card>
      )}

      {!isLoading && eventUUID && rows.length === 0 && !reportData?.error && (
        <Card body className="card--xs card-with-border mt-3">
          <p className="text-muted mb-0">
            No pricing level snapshots yet for this event. Run the pricing level
            snapshot backfill or daily job, then refresh this report.
          </p>
        </Card>
      )}

      {!isLoading && rows.length > 0 && (
        <>
          <div className="mt-3">
            <PricingLevelExecutiveSummary
              executiveSummary={reportData?.executiveSummary}
            />
          </div>

          <div className="mt-3">
            <PricingLevelPerformanceMatrix rows={rows} />
          </div>

          <PricingLevelInsightsRow
            revenueOpportunity={reportData?.revenueOpportunity}
            paceTrend={reportData?.paceTrend}
          />

          <div className="mt-3">
            <PricingLevelForecastedRevenue
              forecastedRevenue={reportData?.forecastedRevenue}
            />
          </div>

          <div className="mt-3">
            <PricingLevelRevenueLeakage
              revenueLeakage={reportData?.revenueLeakage}
            />
          </div>
        </>
      )}

      {!eventUUID && !isLoadingEvents && (
        <Card body className="card--xs card-with-border mt-3">
          <p className="text-muted mb-0">
            Select an event to view pricing level performance.
          </p>
        </Card>
      )}
    </section>
  )
}
